import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, TableCell, TableRow, Typography } from '@mui/material';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { DataTable, useAppSnackbar } from '../../common/TableDesign';
import { LedgerFields } from '../LedgerFormDialog';
import { AccountPicker, FiscalPeriodPicker, FiscalYearPicker } from '../LedgerPickers';
import { getErrorMessage, unwrap } from './BudgetingShared';
import { Download as DownloadIcon } from '@mui/icons-material';

// Vs Actual compares each budget line against posted account balances for
// the year (verified against BudgetService.getVsActual). fiscal_year_id is
// REQUIRED by the backend; period/account are optional scopes. Variance =
// actual - budgeted, so a positive variance on an expense line is an
// overspend. Response is {rows, totals} - not paginated - so this tab fetches
// on demand instead of using LedgerListPage.
const VsActualTab = () => {
  const { can } = useAccessControl();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const [filters, setFilters] = useState({ fiscal_year_id: '', period_id: '', account_id: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!can(RESOURCES.LEDGER.BUDGETING, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Budget vs Actual.</Alert>;
  }

  const load = async () => {
    if (!filters.fiscal_year_id) {
      showSnackbar('Pick a fiscal year first', 'error');
      return;
    }
    try {
      setLoading(true);
      const params = { fiscal_year_id: filters.fiscal_year_id };
      if (filters.period_id) params.period_id = filters.period_id;
      if (filters.account_id) params.account_id = filters.account_id;
      const res = await ledgerAPI.getBudgetVsActual(params);
      setReport(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch variance report'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const totals = report?.totals || {};

  const exportCsv = () => {
    downloadCsv(
      `budget-vs-actual-fy${report?.fiscal_year_id ?? ''}.csv`,
      ['Account', 'Scope', 'Budgeted', 'Actual', 'Variance', 'Utilisation %', 'Approved'],
      rows.map((r) => [
        `${r.account_code || ''} ${r.account_name || ''}`.trim() || `Account ${r.account_id ?? ''}`,
        r.scope || '',
        r.budgeted_amount ?? '',
        r.actual_amount ?? '',
        r.variance ?? '',
        r.utilization_pct ?? '',
        r.approved ? 'Yes' : 'No',
      ])
    );
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <LedgerFields
            fields={[
              { key: 'fiscal_year_id', label: 'Fiscal Year (required)', type: 'picker', picker: FiscalYearPicker, required: true, md: 4 },
              { key: 'period_id', label: 'Period (optional)', type: 'picker', picker: FiscalPeriodPicker, pickerProps: { yearId: filters.fiscal_year_id || undefined }, md: 4 },
              { key: 'account_id', label: 'Account (optional)', type: 'picker', picker: AccountPicker, md: 4 },
            ]}
            values={filters}
            onChange={(key, value) => setFilters((p) => ({ ...p, [key]: value }))}
          />
          <Box display="flex" gap={1} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={load} disabled={loading || !filters.fiscal_year_id}>
              {loading ? 'Loading...' : 'Run Report'}
            </Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!rows.length}>
              Export CSV
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : !report ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              Pick a fiscal year and run the report.
            </Typography>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {report.year_name || `Fiscal year ${report.fiscal_year_id}`} - {rows.length} line{rows.length === 1 ? '' : 's'}
              </Typography>
              <DataTable
                columns={['Account', 'Scope', 'Budgeted', 'Actual', 'Variance', 'Used']}
                data={rows}
                loading={false}
                emptyMessage="No budget lines for this scope."
                renderRow={(r, i) => {
                  const v = Number(r.variance ?? 0);
                  return (
                    <TableRow key={r.budget_id ?? i}>
                      <TableCell>
                        <strong>{r.account_code}</strong> {r.account_name}
                      </TableCell>
                      <TableCell>{r.scope || '-'}</TableCell>
                      <TableCell align="right">{formatAccountingAmount(r.budgeted_amount)}</TableCell>
                      <TableCell align="right">{formatAccountingAmount(r.actual_amount)}</TableCell>
                      <TableCell align="right">
                        <Box component="span" fontWeight={700} color={v > 0 ? 'error.main' : 'success.main'}>
                          {formatAccountingAmount(r.variance)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{r.utilization_pct != null ? `${r.utilization_pct}%` : '-'}</TableCell>
                    </TableRow>
                  );
                }}
              />
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Budgeted: <strong>{formatAccountingAmount(totals.budgeted_amount)}</strong></Typography>
                <Typography variant="body2">Actual: <strong>{formatAccountingAmount(totals.actual_amount)}</strong></Typography>
                <Typography variant="body2">Variance: <strong>{formatAccountingAmount(totals.variance)}</strong></Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {SnackbarComponent}
    </Box>
  );
};

export default VsActualTab;
